import { jsx as _jsx } from "react/jsx-runtime";
import { Children, cloneElement, forwardRef, isValidElement, useEffect, useId, useState } from 'react';
import { cn } from '../utils/cn';
/* Tastatursteuerung nach WAI-ARIA-Tabs-Pattern: Pfeil links/rechts bewegen
   (zyklisch), Home/End springen; der neue Tab wird aktiviert UND fokussiert
   (automatische Aktivierung). Fuer vertikale Tab-Leisten gelten auch
   Pfeil hoch/runter. */
function moveFocus(e, onSelect) {
    const list = e.currentTarget;
    const tabs = Array.from(list.querySelectorAll('[role="tab"]:not([disabled])'));
    if (tabs.length === 0)
        return;
    const current = tabs.findIndex((t) => t === document.activeElement);
    let next = -1;
    switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
            next = current < 0 ? 0 : (current + 1) % tabs.length;
            break;
        case 'ArrowLeft':
        case 'ArrowUp':
            next = current < 0 ? tabs.length - 1 : (current - 1 + tabs.length) % tabs.length;
            break;
        case 'Home':
            next = 0;
            break;
        case 'End':
            next = tabs.length - 1;
            break;
        default:
            return;
    }
    e.preventDefault();
    const target = tabs[next];
    target.focus();
    const value = target.dataset.secTabValue;
    if (value !== undefined) {
        // Zahlenwerte kommen als String aus dem data-Attribut zurueck.
        const raw = target.dataset.secTabValueType === 'number' ? Number(value) : value;
        onSelect?.({ ...e, currentTarget: target, target }, raw);
    }
}
/**
 * Tab-Leiste (MUI-`Tabs`-Ersatz), `role="tablist"`. Kinder sind `<Tab>`;
 * Panels werden mit `<TabPanel value=…>` verknuepft (`aria-controls` /
 * `aria-labelledby`). Pfeiltasten, Home und End wechseln den Tab.
 */
export const Tabs = forwardRef(function Tabs({ value, onChange, className, children, onKeyDown, idBase: idBaseProp, ...rest }, ref) {
    const generated = useId();
    const idBase = idBaseProp ?? generated;
    return (_jsx("div", { ref: ref, role: "tablist", className: cn('sec-tabs', className), onKeyDown: (e) => {
            onKeyDown?.(e);
            if (!e.defaultPrevented)
                moveFocus(e, onChange);
        }, ...rest, children: Children.map(children, (child) => isValidElement(child)
            ? cloneElement(child, { selected: child.props.value === value, onSelect: onChange, idBase })
            : child) }));
});
/** Stabile Element-ids fuer Tab und Panel aus der Leisten-id und dem Wert. */
function tabIds(idBase, value) {
    const safe = String(value).replace(/[^a-zA-Z0-9_-]/g, '_');
    const base = idBase ?? 'sec-tabs';
    return { tab: `${base}-tab-${safe}`, panel: `${base}-panel-${safe}` };
}
/** Einzelner Tab-Button (MUI-`Tab`-Ersatz), `role="tab"`. */
export const Tab = forwardRef(function Tab({ value, label, selected, onSelect, idBase, className, disabled, ...rest }, ref) {
    const ids = tabIds(idBase, value);
    // aria-controls nur, wenn das Panel tatsaechlich im DOM steht (Tabs ohne
    // TabPanel, z. B. Router-gesteuerte Inhalte, sollen keinen toten Verweis tragen).
    const [controls, setControls] = useState(undefined);
    // Ohne Abhaengigkeitsliste: ein <TabPanel> kann nach dem ersten Render
    // dazukommen (Router, Lazy-Loading); setState nur bei Aenderung, daher kein
    // Render-Loop. Die Direktive wandert mit nach dist/ (CRA-ESLint der Apps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const next = typeof document !== 'undefined' && document.getElementById(ids.panel) ? ids.panel : undefined;
        if (next !== controls)
            setControls(next);
    });
    return (_jsx("button", { ref: ref, type: "button", role: "tab", id: ids.tab, "aria-selected": selected, "aria-controls": controls, tabIndex: selected ? 0 : -1, disabled: disabled, "data-sec-tab-value": String(value), "data-sec-tab-value-type": typeof value, className: cn('sec-tab', selected && 'sec-tab--selected', className), onClick: (e) => onSelect?.(e, value), ...rest, children: label }));
});
/**
 * Inhalt eines Tabs (`role="tabpanel"`). Wird per id mit dem `<Tab>` gleichen
 * Werts verknuepft; inaktive Panels sind `hidden`. `tabIndex=0`, damit der
 * Panel-Inhalt nach dem Tab in der Tab-Reihenfolge erreichbar ist.
 *
 * Damit die ids zusammenpassen, `idBase` aus `useTabsId()` an `Tabs` und
 * `TabPanel` geben — oder einfacher: `<Tabs id="x">` + `<TabPanel idBase="x">`.
 */
export const TabPanel = forwardRef(function TabPanel({ value, current, idBase, className, children, ...rest }, ref) {
    const ids = tabIds(idBase, value);
    const active = value === current;
    return (_jsx("div", { ref: ref, role: "tabpanel", id: ids.panel, "aria-labelledby": ids.tab, hidden: !active, tabIndex: active ? 0 : -1, className: cn('sec-tabpanel', className), ...rest, children: active ? children : null }));
});
